// src/factory/symbols/createCableShaft.js
//
// Kablo bacası (cable shaft) factory — useCableShaft.js (interactive) ve
// useTesisatRebuild.js (JBZ load sonrası restore) ortak kullanır.
//
// Pure factory: scene'e eklemez, THREE.Group döndürür. Çağıran scene.add()
// ile sahneye eklemekle sorumludur.

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { createDiagonalLine } from '../../modules/wiring';

/**
 * Kablo bacası 3D nesnesini oluşturur.
 *
 * @param {Object} corner1 - {x, y} ilk köşe
 * @param {Object} corner2 - {x, y} karşı köşe
 * @param {number} [color=0x00ffff]
 * @param {Object} [options]
 * @param {string} [options.id] - Mevcut id (rebuild için); verilmezse yeni üretilir
 * @param {string} [options.shaftGroupId] - Aynı bacanın diğer kat kopyalarıyla eşleşme id'si
 * @param {string} [options.floorPlanId]
 * @param {string} [options.floorPlanName]
 * @returns {THREE.Group}
 */
export function createCableShaftObject(corner1, corner2, color = 0x00ffff, options = {}) {
  const minX = Math.min(corner1.x, corner2.x);
  const maxX = Math.max(corner1.x, corner2.x);
  const minY = Math.min(corner1.y, corner2.y);
  const maxY = Math.max(corner1.y, corner2.y);

  const width = maxX - minX;
  const height = maxY - minY;

  const shaftId = options.id || generateUniqueId();

  const group = new THREE.Group();

  // 1. Arka plan dolgu (tıklanabilir alan)
  const fillShape = new THREE.Shape();
  fillShape.moveTo(minX, minY);
  fillShape.lineTo(maxX, minY);
  fillShape.lineTo(maxX, maxY);
  fillShape.lineTo(minX, maxY);
  fillShape.lineTo(minX, minY);

  const fillGeometry = new THREE.ShapeGeometry(fillShape);
  const fillMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.3,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const fillMesh = new THREE.Mesh(fillGeometry, fillMaterial);
  fillMesh.position.z = 0.05;
  group.add(fillMesh);

  // 2. Border (kalın çizgi)
  const rectPoints = [
    minX, minY, 0.1,
    maxX, minY, 0.1,
    maxX, maxY, 0.1,
    minX, maxY, 0.1,
    minX, minY, 0.1,
  ];

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(rectPoints);

  const lineMaterial = new LineMaterial({
    color,
    linewidth: 3,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
  });

  const line = new Line2(lineGeometry, lineMaterial);
  line.computeLineDistances();
  group.add(line);

  // 3. Çapraz çıkış çizgisi
  const diagonalLine = createDiagonalLine(corner1, corner2, shaftId);
  diagonalLine.userData.isShaftPart = true;
  group.add(diagonalLine);

  group.userData = {
    id: shaftId,
    isSelectable: true,
    type: 'cableShaft',
    start: { x: minX, y: minY, z: 0 },
    width,
    height,
    rotation: 0,
    points: [
      { x: minX, y: minY, z: 0 },
      { x: maxX, y: minY, z: 0 },
      { x: maxX, y: maxY, z: 0 },
      { x: minX, y: maxY, z: 0 },
    ],
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: 0 },
    diagonalStart: { x: corner1.x, y: corner1.y },
    diagonalEnd: { x: corner2.x, y: corner2.y },
    exitCount: 0,
    occupiedExitCount: 0,
    shaftGroupId: options.shaftGroupId || shaftId,
    floorPlanId: options.floorPlanId || null,
    floorPlanName: options.floorPlanName || null,
    connectedCables: [],
  };

  fillMesh.userData = { ...group.userData, isFill: true };
  line.userData = { ...group.userData, isBorder: true };
  diagonalLine.userData = {
    ...diagonalLine.userData,
    ...group.userData,
    isDiagonalLine: true,
    isSelectable: false,
    isGizmo: false,
  };

  return group;
}
